/**
 * InfoItem - Reusable component for displaying label-value pairs with icon
 *
 * Used in modals, overview sections, and detail views
 *
 * @example
 * ```tsx
 * <InfoItem
 *   icon={<HiOutlineEnvelope size={20} />}
 *   label="Email"
 *   value={email}
 * />
 *
 * <InfoItem
 *   icon={<HiOutlinePhone size={20} />}
 *   label="Phone"
 *   value={phone}
 *   valueAsLink={{ href: `tel:${phone}` }}
 * />
 *
 * <InfoItem
 *   icon={<HiOutlineCheckCircle size={20} />}
 *   label="Status"
 *   valueAsBadge={{ tone: "success", children: "Active" }}
 * />
 * ```
 */

"use client";

import { Box, Flex, Link } from "@chakra-ui/react";
import type { ReactElement, ReactNode } from "react";
import { Badge } from "../../ui/Badge";
import { Text } from "../../ui/Text";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface InfoItemProps {
  /**
   * Icon to display (React element)
   */
  icon?: ReactElement;
  /**
   * Label text
   */
  label: string;
  /**
   * Value to display (text)
   */
  value?: string | ReactNode;
  /**
   * Display value as a link
   */
  valueAsLink?: {
    href: string;
    children?: string;
  };
  /**
   * Display value as a badge
   */
  valueAsBadge?: {
    tone?: "success" | "neutral" | "info" | "premium" | "danger";
    children: ReactNode;
    fontSize?: string;
  };
  /**
   * Whether to show icon in a colored box (default: true)
   */
  showIconBox?: boolean;
  /**
   * Custom icon box size (default: 20)
   */
  iconSize?: number;
  /**
   * Layout direction (default: "row")
   */
  direction?: "row" | "column";
}

export const InfoItem = ({
  icon,
  label,
  value,
  valueAsLink,
  valueAsBadge,
  showIconBox = true,
  iconSize = 20,
  direction = "row",
}: InfoItemProps) => {
  const theme = useThemeTokens();

  const renderValue = () => {
    if (valueAsBadge) {
      return (
        <Badge
          tone={valueAsBadge.tone || "neutral"}
          fontSize={valueAsBadge.fontSize || "11px"}
          w="fit-content"
        >
          {valueAsBadge.children}
        </Badge>
      );
    }

    if (valueAsLink) {
      return (
        <Link
          href={valueAsLink.href}
          fontSize="sm"
          color={theme.accent.muted}
          fontWeight="500"
          sx={{
            "@media (hover: hover)": {
              "&:hover": {
                textDecoration: "underline",
                color: theme.accent.primary,
              },
            },
          }}
          transition="color 0.2s"
          wordBreak="break-word"
        >
          {valueAsLink.children || value}
        </Link>
      );
    }

    if (value !== undefined) {
      return (
        <Text fontSize="sm" color={theme.text.primary} fontWeight="500" wordBreak="break-word">
          {value}
        </Text>
      );
    }

    return (
      <Text fontSize="sm" color={theme.text.tertiary}>
        -
      </Text>
    );
  };

  if (direction === "column") {
    // Vertical layout (used in SimpleGrid)
    return (
      <Box>
        {icon && (
          <Flex align="center" gap={2} mb={1.5}>
            {showIconBox ? (
              <Box
                p={2.5}
                borderRadius="10px"
                bg={theme.accent.primaryBg}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color={theme.accent.primary}
                flexShrink={0}
              >
                {icon}
              </Box>
            ) : (
              <Box color={theme.text.secondary} flexShrink={0}>
                {icon}
              </Box>
            )}
            <Text fontSize="xs" variant="secondary" fontWeight="500">
              {label}
            </Text>
          </Flex>
        )}
        {!icon && (
          <Text fontSize="xs" variant="secondary" mb={1.5} fontWeight="500">
            {label}
          </Text>
        )}
        {renderValue()}
      </Box>
    );
  }

  // Horizontal layout (default)
  return (
    <Flex align="flex-start" gap={3}>
      {icon && showIconBox && (
        <Box
          p={2.5}
          borderRadius="10px"
          bg={theme.accent.primaryBg}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={theme.accent.primary}
          flexShrink={0}
        >
          {icon}
        </Box>
      )}
      {icon && !showIconBox && (
        <Box color={theme.text.secondary} flexShrink={0}>
          {icon}
        </Box>
      )}
      <Box flex="1" minW={0}>
        <Text fontSize="xs" variant="secondary" mb={1} fontWeight="500">
          {label}
        </Text>
        {renderValue()}
      </Box>
    </Flex>
  );
};

